import { z } from "zod";
export const userRoleSchema = z.enum(["client", "staff", "partner"]);
export const sessionAuthMethodSchema = z.enum(["eid", "passkey", "email_otp"]);
// Kept local rather than importing @ferrivia/i18n's localeSchema: that
// package is a browser React package (react-i18next, no build step) and
// api-contract is also consumed server-side by apps/api, so it stays
// dependency-free of react. Same duplication precedent as userRoleSchema
// existing separately in @ferrivia/db.
export const localeSchema = z.enum(["fi", "en"]);
export const userSchema = z.object({
    id: z.string(),
    role: userRoleSchema,
    email: z.string().nullable(),
    // `.default(null)` rather than plain `.nullable()`: client and api deploy
    // as two separate, unsynchronized Cloud Run rollouts (deploy-client.yml
    // and deploy-api.yml both fire on push to main, in parallel — docs/ci.md),
    // and every PR preview talks to the shared dev API regardless of what the
    // branch itself changed (docs/ci.md, "Previews always point at the dev
    // API"). A newer client can therefore hit an api that predates these two
    // fields at any time, not just during this rollout — parsing a response
    // that simply omits them must still succeed rather than throwing and
    // getting misread as a session/handoff failure (see AuthCallback.tsx's
    // catch-all). `User.phone`/`preferredName` stay `string | null` either
    // way; `.default(null)` only changes what an absent key parses to.
    phone: z.string().nullable().default(null),
    preferredName: z.string().nullable().default(null),
    // The sign-in identity's given name: eID-asserted in Finland, typed at
    // onboarding in the US. Only GET/PATCH /me carry it; the sign-in responses
    // omit it. Optional rather than `.default(null)` so a User built without it
    // (every sign-in response, every fixture) still type-checks.
    givenName: z.string().nullable().optional(),
    // When the account was created. Only GET/PATCH /me carry it, optional for
    // the same reason as givenName. Home's greeting reads it to tell a first
    // visit from a return.
    createdAt: z.string().optional(),
    locale: localeSchema.nullable(),
    authMethod: sessionAuthMethodSchema,
});
// null clears a field — a legitimate "I don't want to give a phone number
// anymore". Unlike onboarding's own email step, phone is not format-validated
// beyond length: Finnish and international formats vary too much to reject
// safely. Mirrors patientContactUpdateSchema (patients.ts), the clinic's
// staff-editing-a-customer analogue of this same self-editing request.
export const updateMeRequestSchema = z
    .object({
    locale: localeSchema,
    preferredName: z.string().trim().min(1).max(100).nullable(),
    email: z.string().trim().email().nullable(),
    phone: z.string().trim().min(1).max(40).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
export const eidStartRequestSchema = z.object({
    returnTo: z.string().url(),
});
export const eidStartResponseSchema = z.object({
    authorizationUrl: z.string().url(),
});
export const eidHandoffRequestSchema = z.object({
    code: z.string().min(1),
});
export const emailOtpStartRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
});
export const emailOtpVerifyRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    code: z.string().regex(/^\d{6}$/),
});
export const sessionResponseSchema = z.object({
    token: z.string(),
    expiresAt: z.string(),
    user: userSchema,
});
export const meResponseSchema = userSchema;
export const passkeySummarySchema = z.object({
    id: z.string(),
    nickname: z.string().nullable(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
});
// The register/login *verify* request bodies are WebAuthn browser API JSON
// (@simplewebauthn/{server,browser} produce and consume the same shape on
// either side). Real validation happens server-side in
// verifyRegistrationResponse / verifyAuthenticationResponse, which rejects
// anything malformed or forged regardless of what passes this schema — this
// is a shape check plus a type label for the client.
const webauthnCredentialShape = z.object({
    id: z.string().min(1),
    rawId: z.string().min(1),
    type: z.literal("public-key"),
    clientExtensionResults: z.record(z.unknown()),
    response: z.record(z.unknown()),
    authenticatorAttachment: z.string().optional(),
});
export const passkeyRegistrationVerifySchema = webauthnCredentialShape;
export const passkeyLoginVerifySchema = webauthnCredentialShape;
// The options responses are server-generated (by @simplewebauthn/server) and
// consumed as-is by @simplewebauthn/browser on the client — nothing to
// validate, just a type label.
export const passkeyRegistrationOptionsSchema = z.custom();
export const passkeyLoginOptionsSchema = z.custom();
